"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import { countriesData } from "@/data/countriesData";

// India (Origin)
const origin = {
  x: 69,
  y: 42
};

// Approximate positions on the low-res world map (% from top-left)
const mapPositions = {
  mauritius: {
    x: 63,
    y: 66
  },
  guyana: {
    x: 31,
    y: 52
  },
  "trinidad-tobago": {
    x: 30,
    y: 49
  },
  jamaica: {
    x: 26,
    y: 46
  },
  grenada: {
    x: 28.5,
    y: 48
  },
  "saint-lucia": {
    x: 29,
    y: 48.5
  },
  "saint-kitts": {
    x: 27.5,
    y: 47.5
  },
  "saint-vincent": {
    x: 29.5,
    y: 48.5
  },
  nevis: {
    x: 27.8,
    y: 47.8
  },
  "south-africa": {
    x: 56,
    y: 73
  },
  fiji: {
    x: 96,
    y: 62
  },
  malaysia: {
    x: 79,
    y: 54
  },
  "east-africa": {
    x: 60,
    y: 56
  },
  seychelles: {
    x: 63,
    y: 62
  },
  reunion: {
    x: 62,
    y: 67
  },
  suriname: {
    x: 31.5,
    y: 52.5
  }
};
const destinations = countriesData.map(country => ({
  name: country.name,
  slug: country.slug,
  ...mapPositions[country.slug]
}));
const WorldMap = () => {
  const $ = _c(25);
  const [hoveredCountry, setHoveredCountry] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6"><div className="w-8 h-px bg-[#DF6D2A]" /><span className="font-body text-[10.5px] tracking-[0.25em] uppercase text-accent-readable font-light">Global Footprint</span></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h2 className="font-display text-[34px] sm:text-[44px] md:text-[56px] font-light text-[#F4EFE6] leading-[1.05] mb-5 sm:mb-8">The Girmitiya <br /><em className="text-[#C9A84C] italic">Diaspora</em></h2>;
    t2 = <p className="font-body text-[14px] sm:text-[15px] font-light text-white/60 leading-[1.7] sm:leading-[1.8] mb-8 sm:mb-12 max-w-[400px]">From 1834 to 1917, approximately 1.3 million Indians crossed the oceans under the Girmitiya system. Explore the colonies shaped by their resilience, labour, and vibrant cultural legacy.</p>;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== hoveredCountry) {
    t3 = destinations.map((dest, i) => <Link href={`/countries/${dest.slug}`} key={i} onMouseEnter={() => setHoveredCountry(dest.name)} onMouseLeave={() => setHoveredCountry(null)} className="group cursor-pointer flex items-center gap-3"><div className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${hoveredCountry === dest.name ? "bg-[#DF6D2A] shadow-[0_0_8px_#DF6D2A]" : "bg-white/20 group-hover:bg-[#C9A84C]"}`} /><span className={`font-body text-[11px] sm:text-[13px] tracking-[0.03em] sm:tracking-[0.05em] uppercase transition-colors duration-300 ${hoveredCountry === dest.name ? "text-[#F4EFE6]" : "text-white/40 group-hover:text-white/80"}`}>{dest.name}</span></Link>);
    $[3] = hoveredCountry;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <div className="w-full lg:w-[35%] flex flex-col">{t0}{t1}{t2}<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-y-3 sm:gap-y-4 gap-x-3 sm:gap-x-4">{t3}</div></div>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="absolute inset-0 world-mask bg-[#F4EFE6] opacity-10" />;
    t6 = <div className="absolute left-[69%] top-[42%] -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-[100%] aspect-square rounded-full border border-[#DF6D2A]/5 opacity-50 pointer-events-none" />;
    t7 = <div className="absolute left-[69%] top-[42%] -translate-x-1/2 -translate-y-1/2 w-[60%] aspect-square rounded-full border border-[#DF6D2A]/5 opacity-50 pointer-events-none" />;
    t8 = <div className="absolute left-[69%] top-[42%] -translate-x-1/2 -translate-y-1/2 w-[30%] aspect-square rounded-full border border-[#DF6D2A]/10 opacity-50 pointer-events-none" />;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      left: `${origin.x}%`,
      top: `${origin.y}%`,
      transform: "translate(-50%, -50%)"
    };
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="absolute w-3 h-3 bg-[#DF6D2A] rounded-full z-30 shadow-[0_0_15px_#DF6D2A]" style={t9}><div className="absolute inset-0 rounded-full bg-[#DF6D2A] opacity-40 motion-reduce:animate-none animate-ping" style={{
        animationDuration: "2.5s"
      }} /><span className="absolute -top-5 sm:-top-6 left-1/2 -translate-x-1/2 font-body text-[8px] sm:text-[10px] tracking-widest text-[#DF6D2A] uppercase bg-[#0F1C2E]/80 px-1.5 sm:px-2 py-0.5 rounded border border-[#DF6D2A]/30 whitespace-nowrap">Origin</span></div>;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== hoveredCountry) {
    t11 = destinations.map((dest_0, i_0) => {
      const isHovered = hoveredCountry === dest_0.name;
      return <line key={i_0} x1={`${origin.x}%`} y1={`${origin.y}%`} x2={`${dest_0.x}%`} y2={`${dest_0.y}%`} stroke={isHovered ? "#DF6D2A" : "#F4EFE6"} strokeWidth={isHovered ? 1.5 : 0.5} strokeDasharray="4 4" opacity={isHovered ? 0.75 : 0.08} className="transition-opacity duration-300" />;
    });
    $[13] = hoveredCountry;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t11) {
    t12 = <svg className="absolute inset-0 w-full h-full pointer-events-none z-20">{t11}</svg>;
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== hoveredCountry) {
    t13 = destinations.map((dest_1, i_1) => {
      const isHovered_0 = hoveredCountry === dest_1.name;
      return <Link href={`/countries/${dest_1.slug}`} key={i_1} aria-label={`View ${dest_1.name} Girmitiya history`} onMouseEnter={() => setHoveredCountry(dest_1.name)} onMouseLeave={() => setHoveredCountry(null)} className="absolute z-30 cursor-pointer group flex items-center justify-center min-w-[44px] min-h-[44px] -m-[18px] sm:-m-[20px]" style={{
        left: `${dest_1.x}%`,
        top: `${dest_1.y}%`,
        transform: "translate(-50%, -50%)"
      }}><motion.div className={`w-2.5 sm:w-3 h-2.5 sm:h-3 rounded-full transition-all duration-300 ${isHovered_0 ? "bg-[#DF6D2A] scale-150 shadow-[0_0_12px_#DF6D2A]" : "bg-[#C9A84C] scale-75 opacity-60 group-hover:scale-100 group-hover:opacity-100"}`} /><div className={`absolute top-3 sm:top-4 left-1/2 -translate-x-1/2 font-body text-[9px] sm:text-[11px] tracking-wider sm:tracking-widest text-[#F4EFE6] uppercase bg-[#0F1C2E]/90 backdrop-blur-sm px-2 sm:px-3 py-1 sm:py-1.5 border transition-all duration-300 pointer-events-none whitespace-nowrap ${isHovered_0 ? "opacity-100 translate-y-0 border-[#DF6D2A]/50 z-40" : "opacity-0 -translate-y-2 border-white/10 z-30"}`}>{dest_1.name}</div></Link>;
    });
    $[17] = hoveredCountry;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t12 || $[20] !== t13) {
    t14 = <div className="w-full lg:w-[65%] relative aspect-[4/3] sm:aspect-[16/10] md:aspect-[2/1] lg:aspect-[16/10] flex items-center justify-center overflow-hidden">{t5}{t6}{t7}{t8}{t10}{t12}{t13}</div>;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t14 || $[23] !== t4) {
    t15 = <><section id="global-footprint" className="bg-[#0F1C2E] py-16 sm:py-24 md:py-32 relative overflow-hidden border-t border-[#F4EFE6]/10 map-grain"><div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 relative z-20"><div className="flex flex-col lg:flex-row gap-10 sm:gap-16 lg:gap-8 items-center">{t4}{t14}</div></div></section></>;
    $[22] = t14;
    $[23] = t4;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  return t15;
};
export default WorldMap;